import * as React from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Lock, Search, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { moveOptionFocus, usePopover } from "./usePopover";

/**
 * A many-of dropdown. `FilterSelect`'s sibling, and it shares that component's
 * panel, search box, grouping and keyboard rules - read its header first; only
 * what differs is explained here.
 *
 * ── The trigger is one pill and a count, and its width never moves ──────────
 *
 * A multi-select whose trigger grows with the selection is a control that
 * reshuffles the form around it every time somebody ticks a box, and in a filter
 * bar it pushes its neighbours off the row. So the trigger is a fixed width and
 * shows **the first selected option as a pill, then `+N` for the rest**. The
 * pill truncates; the full list is in the panel, which is where anyone
 * comparing names is looking anyway.
 *
 * ── The pills do not have remove buttons, deliberately ──────────────────────
 *
 * The obvious version puts an × inside each pill. But the trigger is a
 * `<button>`, and a button inside a button is invalid HTML that browsers
 * silently restructure; the usual workaround, a `<span role="button">`, is
 * unreachable by keyboard. That exact trade was already made and reverted once
 * in `DatePicker`. Unticking in the panel is one extra click, works from the
 * keyboard, and is where the user's attention already is when they are changing
 * a selection.
 *
 * ── Locked options ─────────────────────────────────────────────────────────
 *
 * `locked` is always selected and cannot be turned off, and it is **not**
 * `disabled`: the value is real and chosen, it simply is not this control's to
 * revoke. A user's default site is the case that exists - Postgres refuses to
 * delete that membership (`app.protect_home_site_membership`) rather than
 * quietly moving their default, so an unticked box would produce a save that
 * fails for a reason the form never mentioned. It renders with a padlock and
 * `title` saying why, which is the honest version of greying it out.
 *
 * A locked value is always present in what `onChange` emits, whatever the caller
 * passed in `value` - so a call site cannot forget to add it back.
 */
export interface MultiOption {
  value: string;
  label: string;
  hint?: string;
  group?: string;
  /** Always on, never removable here. See the header. */
  locked?: boolean;
  /** Shown on the padlock. Say what would have to change, not that it is locked. */
  lockReason?: string;
  /** Shown but not choosable, for a value that exists and is unavailable. */
  disabled?: boolean;
}

const SEARCH_THRESHOLD = 6;

export function MultiSelect({
  value,
  options,
  onChange,
  label,
  placeholder = "None selected",
  searchable,
  searchPlaceholder = "Search",
  emptyLabel = "No matches",
  align = "start",
  invalid,
  disabled,
  id,
  className,
}: {
  value: readonly string[];
  options: readonly MultiOption[];
  onChange: (values: string[]) => void;
  /** The accessible name. The trigger only shows the selection. */
  label: string;
  placeholder?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  emptyLabel?: string;
  align?: "start" | "end";
  invalid?: boolean;
  disabled?: boolean;
  /** So a `Field`'s `<label htmlFor>` reaches the trigger. */
  id?: string;
  className?: string;
}) {
  const { open, toggle, close, rootRef, triggerRef, panelRef, panelStyle, panelTarget } =
    usePopover({
      align,
      matchTriggerWidth: true,
    });
  const [query, setQuery] = React.useState("");
  const searchRef = React.useRef<HTMLInputElement>(null);

  const showSearch = searchable ?? options.length > SEARCH_THRESHOLD;

  // Locked values count as selected whether or not the caller included them, so
  // the trigger cannot disagree with what a save would actually send.
  const selected = React.useMemo(() => {
    const set = new Set(value);
    for (const option of options) if (option.locked) set.add(option.value);
    return set;
  }, [value, options]);

  // In the options' own order rather than in selection order: the pill on the
  // trigger then stays the same one as the list is ticked and unticked further
  // down, instead of changing to whatever was clicked most recently.
  const chosen = React.useMemo(
    () => options.filter((option) => selected.has(option.value)),
    [options, selected]
  );

  const matches = React.useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return options;
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(term) ||
        (option.hint ?? "").toLowerCase().includes(term)
    );
  }, [options, query]);

  React.useEffect(() => {
    if (open) {
      setQuery("");
      if (showSearch) {
        const frame = requestAnimationFrame(() => searchRef.current?.focus());
        return () => cancelAnimationFrame(frame);
      }
    }
  }, [open, showSearch]);

  function toggleOption(option: MultiOption) {
    if (option.locked || option.disabled) return;

    const next = new Set(selected);
    if (next.has(option.value)) next.delete(option.value);
    else next.add(option.value);

    // Emitted in the options' order, so two equal selections are always the same
    // array and a `dirty` check by value does not fire on a reordering.
    onChange(options.filter((o) => next.has(o.value)).map((o) => o.value));
  }

  // The panel stays open on every toggle. Choosing several things is the entire
  // point of the control, and a panel that closed after each one would make the
  // second choice a fresh open, a fresh search and a fresh scroll.
  return (
    <div ref={rootRef} className={cn("msel", className)}>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        title={chosen.map((option) => option.label).join(", ") || undefined}
        onClick={toggle}
        onKeyDown={(event) => {
          if (!open && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
            event.preventDefault();
            toggle();
          }
        }}
        className={cn("fsel__trigger", "fsel__trigger--field", invalid && "fsel__trigger--invalid")}
      >
        <span className="msel__summary">
          {chosen.length === 0 ? (
            <span className="fsel__value fsel__value--placeholder">{placeholder}</span>
          ) : (
            <>
              <span className="msel__pill">{chosen[0].label}</span>
              {chosen.length > 1 && <span className="msel__more">+{chosen.length - 1}</span>}
            </>
          )}
        </span>
        <ChevronDown aria-hidden className={cn("fsel__chevron", open && "fsel__chevron--open")} />
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            // See the "top layer" note in `usePopover.ts` - this is what lets
            // the panel render over an open `Modal`/`Drawer`.
            popover="manual"
            role="listbox"
            aria-multiselectable
            aria-label={label}
            style={panelStyle}
            onKeyDown={(event) => {
              if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                event.preventDefault();
                moveOptionFocus(panelRef.current, event.key === "ArrowDown" ? 1 : -1);
              }
            }}
            className="fsel__panel"
          >
            {showSearch && (
              <div className="fsel__search">
                <Search aria-hidden className="fsel__search-icon" />
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  aria-label={`Search ${label}`}
                  placeholder={searchPlaceholder}
                  onChange={(event) => setQuery(event.target.value)}
                  className="fsel__search-input"
                />
                {query && (
                  <button
                    type="button"
                    aria-label="Clear search"
                    onClick={() => {
                      setQuery("");
                      searchRef.current?.focus();
                    }}
                    className="fsel__search-clear"
                  >
                    <X aria-hidden />
                  </button>
                )}
              </div>
            )}

            <div className="fsel__list">
              {matches.length === 0 && <p className="fsel__empty">{emptyLabel}</p>}

              {matches.map((option, index) => {
                const isSelected = selected.has(option.value);
                const heading = option.group && option.group !== matches[index - 1]?.group;

                return (
                  <React.Fragment key={option.value}>
                    {heading && <p className="fsel__group">{option.group}</p>}
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      disabled={option.disabled}
                      title={option.locked ? option.lockReason : undefined}
                      onClick={() => toggleOption(option)}
                      className={cn(
                        "fsel__option",
                        isSelected && "fsel__option--selected",
                        option.locked && "fsel__option--locked"
                      )}
                    >
                      <span className="fsel__option-text">
                        <span className="fsel__option-label">{option.label}</span>
                        {option.hint && <span className="fsel__option-hint">{option.hint}</span>}
                      </span>
                      {option.locked ? (
                        <Lock aria-hidden className="msel__lock" />
                      ) : (
                        isSelected && <Check aria-hidden className="fsel__check" />
                      )}
                    </button>
                  </React.Fragment>
                );
              })}
            </div>

            {/* Nothing closes this panel but Escape, an outside click or the
                trigger, so it needs a way out that reads as one. Tab from the
                last option would otherwise walk into the page behind it. */}
            <div className="fsel__footer">
              <button type="button" className="fsel__done" onClick={() => close(true)}>
                Done
              </button>
            </div>
          </div>,
          panelTarget
        )}
    </div>
  );
}
